import DoubleArrowIcon from '@mui/icons-material/DoubleArrow';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Button,
  Grid,
  Link,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import { Box, Container } from '@mui/system';

import DynamicBreadcrumbs from '@/components/nav/Breadcrumb';
import ResourcesRightSidebar from '@/components/nav/ResourcesRightSidebar';
import AppLayout from '@/layouts/AppLayout';
import { Meta } from '@/layouts/Meta';

const FleetManagers = () => {
  return (
    <AppLayout
      meta={
        <Meta
          title="Form 2290 Filing Made Easy for Fleet Managers"
          description="Effortlessly file Form 2290 for multiple vehicles. TaxZerone helps fleet managers stay compliant, avoid penalties, and file on time with ease."
          keywords=""
          canonical="resources/excise-tax/form-2290-filing-guide-for-fleet-managers/"
          ogType="article"
          jsonLdPricing="19.99"
          jsonLdAggregateRatingValue="4.97"
          jsonLdAggregateRatingCount="3830"
        />
      }
    >
      <Container maxWidth="lg" sx={{ textAlign: 'center' }}>
        <Typography
          variant="h1"
          sx={{
            fontSize: { lg: 40, md: 36, sm: 32, xs: 24 },
            fontWeight: 600,
            mt: 3,
          }}
        >
          Form 2290 Filing Guide for{' '}
          <span className="highlight-container">
            <span className="highlight">Fleet Managers</span>
          </span>{' '}
        </Typography>
        <Typography
          variant="body1"
          sx={{
            fontSize: { lg: 20, md: 18, sm: 16, xs: 16 },
            fontWeight: 400,
            mt: 1,
            mb: 2,
          }}
        >
          Use TaxZerone to streamline your HVUT filing and maintain IRS
          compliance!
        </Typography>
        <Box sx={{ textAlign: 'center', mb: 4 }}>
          <Button
            variant="contained"
            className="btn-primary medium"
            sx={{
              fontSize: {
                xs: '16px !important',
                sm: '18px !important',
                lg: '24px !important',
              },
              transition: 'all 0.3s ease 0s',
              '&:hover, &:focus, &:active': { transform: 'scale(1.1)' },
            }}
            href={`${process.env.NEXT_PUBLIC_APPLICATION_URL}signup/?ref=fleet`}
          >
            E-file Now!
          </Button>
        </Box>
      </Container>
      <Grid
        container
        sx={{
          backgroundColor: '#f0f2f9',
          py: 5,
          mx: '0 !important',
          width: '100% !important',
        }}
        spacing={2}
        m={0}
        mt={0}
      >
        <Container maxWidth="lg" sx={{ mb: 0 }}>
          <DynamicBreadcrumbs />
          <Grid
            container
            spacing={0}
            rowSpacing={0}
            m={0}
            sx={{ flexDirection: 'row-reverse' }}
          >
            <Grid
              size={{ xs: 12, md: 3.5, sm: 4.5 }}
              sx={{ display: { xs: 'none', sm: 'block' } }}
            >
              <ResourcesRightSidebar />
            </Grid>
            <Grid size={{ xs: 12, md: 8.5, sm: 7.5 }}>
              <Paper
                variant="outlined"
                sx={{
                  border: '1px solid #ccc',
                  p: 3,
                  px: 3,
                  textAlign: 'left',
                  mb: 0,
                }}
              >
                {/* <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 30, md: 24, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  What is IRS Form 2290?
                </Typography> */}
                <Stack
                  direction={{ xs: 'column-reverse', md: 'row' }}
                  spacing={{ xs: 1, sm: 2, md: 3 }}
                  sx={{ mb: 0 }}
                >
                  <Box>
                    <Box
                      itemScope
                      itemType="https://schema.org/Article"
                      sx={{
                        p: 3,
                        px: 3,
                        backgroundColor: '#f0f5f8',
                        borderLeft: '5px solid #01456c',
                        mb: 4,
                        borderRadius: 3,
                        position: 'relative',
                      }}
                    >
                      <link
                        itemProp="mainEntityOfPage"
                        href="https://www.taxzerone.com/resources/excise-tax/form-2290-filing-guide-for-fleet-managers/"
                      />

                      {/* Article requires an image */}
                      <div
                        itemProp="image"
                        itemScope
                        itemType="https://schema.org/ImageObject"
                        style={{ display: 'none' }}
                      >
                        <link
                          itemProp="url"
                          href="https://www.taxzerone.com/assets/images/logo.webp"
                        />
                        <data itemProp="width" value="380" />
                        <data itemProp="height" value="124" />
                      </div>

                      <Box
                        sx={{
                          display: 'grid',
                          justifyContent: 'flex-start',
                          alignItems: 'center',
                          textAlign: 'left',
                          mb: 2,
                        }}
                      >
                        <Typography variant="body2">
                          Last updated:{' '}
                          <time
                            itemProp="datePublished"
                            dateTime="2026-08-21T09:00:00-05:00"
                          >
                            August 21, 2026
                          </time>
                          <time
                            itemProp="dateModified"
                            dateTime="2026-08-21T09:00:00-05:00"
                            style={{ display: 'none' }}
                          >
                            August 21, 2026
                          </time>
                        </Typography>

                        <Typography
                          variant="body2"
                          sx={{ fontWeight: 600 }}
                          itemProp="author"
                          itemScope
                          itemType="https://schema.org/Person"
                        >
                          -by{' '}
                          <span itemProp="name">
                            <a
                              itemProp="url"
                              href={`${process.env.NEXT_PUBLIC_HOMESITE_URL}`}
                              style={{
                                color: 'inherit',
                                textDecoration: 'none',
                              }}
                            >
                              Alexia Zepeda
                            </a>
                          </span>{' '}
                          | TaxZerone
                        </Typography>
                      </Box>

                      <Typography
                        variant="h4"
                        itemProp="headline"
                        sx={{
                          fontWeight: 600,
                          mb: 2,
                          fontSize: { md: 20, sm: 18, xs: 16 },
                          color: '#01456c',
                          fontStyle: 'italic',
                        }}
                      >
                        Fleet Managers Form 2290 Filing at a Glance
                      </Typography>

                      <Box
                        component="ul"
                        itemProp="articleBody"
                        sx={{
                          mb: 0,
                          pl: 0,
                          listStyle: 'none',

                          '& li': {
                            mb: 1.6,
                            position: 'relative',
                            pl: 3,
                          },

                          '& li::before': {
                            content: '"★"',
                            position: 'absolute',
                            left: 0,
                            top: '-2px',
                            color: '#01456c',
                            fontSize: '18px',
                          },
                        }}
                      >
                        <li>
                          <span>
                            <strong style={{ fontWeight: 600 }}>
                              Who Needs to File:
                            </strong>{' '}
                            Fleet operators with heavy vehicles having a taxable
                            gross weight of{' '}
                            <strong style={{ fontWeight: 600 }}>
                              55,000 pounds or more
                            </strong>{' '}
                            that operate on public highways.
                          </span>
                        </li>

                        <li>
                          <span>
                            <strong style={{ fontWeight: 600 }}>
                              Suspended Vehicles:
                            </strong>{' '}
                            Vehicles driven{' '}
                            <strong style={{ fontWeight: 600 }}>
                              5,000 miles or less
                            </strong>{' '}
                            (7,500 miles for agricultural vehicles) may qualify
                            for suspended tax status.
                          </span>
                        </li>

                        <li>
                          <span>
                            <strong style={{ fontWeight: 600 }}>
                              Filing Requirements:
                            </strong>{' '}
                            Report each vehicle’s{' '}
                            <strong style={{ fontWeight: 600 }}>
                              VIN, taxable gross weight, and first-used month
                            </strong>{' '}
                            accurately.
                          </span>
                        </li>

                        <li>
                          <span>
                            <strong style={{ fontWeight: 600 }}>
                              Corrections &amp; Amendments:
                            </strong>{' '}
                            File VIN corrections or amendments when vehicle
                            information, mileage, or taxable weight changes.
                          </span>
                        </li>

                        <li>
                          <span>
                            <strong style={{ fontWeight: 600 }}>
                              Schedule 1:
                            </strong>{' '}
                            Keep your{' '}
                            <Link
                              href="/resources/excise-tax/form-2290-schedule-1/"
                              sx={{ color: '#0202ff' }}
                            >
                              IRS-stamped Schedule 1
                            </Link>{' '}
                            as proof of filing for vehicle registration and
                            renewal.
                          </span>
                        </li>

                        <li>
                          <span>
                            <strong style={{ fontWeight: 600 }}>
                              Credits &amp; Refunds:
                            </strong>{' '}
                            Eligible fleet operators may claim credits or
                            refunds using{' '}
                            <Link
                              href="https://www.taxzerone.com/resources/excise-tax/form-8849-schedule-6-instructions/"
                              sx={{ color: '#0202ff' }}
                            >
                              Form 8849
                            </Link>{' '}
                            for qualifying vehicles.
                          </span>
                        </li>
                      </Box>

                      <div
                        itemProp="publisher"
                        itemScope
                        itemType="https://schema.org/Organization"
                        style={{ display: 'none' }}
                      >
                        <span itemProp="name">TaxZerone</span>

                        <div
                          itemProp="logo"
                          itemScope
                          itemType="https://schema.org/ImageObject"
                        >
                          <link
                            itemProp="url"
                            href="https://www.taxzerone.com/assets/images/logo.webp"
                          />
                          <data itemProp="width" value="380" />
                          <data itemProp="height" value="124" />
                        </div>
                      </div>
                    </Box>

                    <Typography variant="body1" sx={{ mb: 2 }}>
                      Fleet managers have a unique responsibility for staying on
                      top of tax rules, particularly IRS Form{' '}
                      <Link
                        href="/excise-tax/efile-irs-form-2290-heavy-vehicle-use-tax-online/"
                        sx={{ color: '#0202ff' }}
                      >
                        2290 Heavy Vehicle Use Tax
                      </Link>{' '}
                      forms (HVUT). Multiple vehicle filings require accuracy,
                      record keeping, and timely submission to prevent penalties
                      and registration delay.
                    </Typography>
                    <Typography variant="body1" sx={{ mb: 0 }}>
                      This comprehensive guide is designed specifically for
                      fleet managers and business owners, with an easy and clear
                      way to meet your HVUT requirements. With the right
                      approach and tools, Form 2290 filing can be made easier,
                      allowing you to stay compliant without losing focus on
                      maintaining long-term success in your fleet business.
                    </Typography>
                  </Box>
                </Stack>
                <Box
                  sx={{
                    pt: 2,
                  }}
                >
                  <Typography variant="body1" sx={{ mb: 0.5 }}>
                    <b>Table of Contents</b>
                  </Typography>
                  <List
                    sx={{ mb: 5, border: '1px solid #ccc', borderRadius: 2 }}
                  >
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#fleet" sx={{ color: '#0202ff' }}>
                          Form 2290 for Fleet Operators
                        </Link>
                      </ListItemText>
                    </ListItem>
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#why" sx={{ color: '#0202ff' }}>
                          Why Fleet Managers Need to File Form 2290?
                        </Link>
                      </ListItemText>
                    </ListItem>
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#manage" sx={{ color: '#0202ff' }}>
                          Managing Multiple Vehicle Categories
                        </Link>
                      </ListItemText>
                    </ListItem>
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#howto" sx={{ color: '#0202ff' }}>
                          How to Handle Taxable Vehicles?
                        </Link>
                      </ListItemText>
                    </ListItem>
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#handle" sx={{ color: '#0202ff' }}>
                          How to Handle Suspended Vehicles?
                        </Link>
                      </ListItemText>
                    </ListItem>
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#errors" sx={{ color: '#0202ff' }}>
                          Correcting Errors: VIN Corrections and Amendments
                        </Link>
                      </ListItemText>
                    </ListItem>
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#claim" sx={{ color: '#0202ff' }}>
                          Claiming Credits and Refunds with Form 8849
                        </Link>
                      </ListItemText>
                    </ListItem>
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#helps" sx={{ color: '#0202ff' }}>
                          How TaxZerone helps Fleet Managers?
                        </Link>
                      </ListItemText>
                    </ListItem>
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#best" sx={{ color: '#0202ff' }}>
                          Best Practices for tax Form 2290 Filing
                        </Link>
                      </ListItemText>
                    </ListItem>
                  </List>
                </Box>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 20, sm: 18, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                    scrollMarginTop: 87,
                  }}
                  id="fleet"
                >
                  Form 2290 for Fleet Operators
                </Typography>

                <Typography variant="body1" sx={{ mb: 2 }}>
                  {' '}
                  <Link
                    href="/excise-tax/efile-irs-form-2290-heavy-vehicle-use-tax-online/"
                    sx={{ color: '#0202ff' }}
                  >
                    Form 2290
                  </Link>{' '}
                  is used to pay and report the Heavy Vehicle Use Tax (HVUT) on
                  any vehicle with{' '}
                  <Link
                    href="/resources/excise-tax/form-2290-taxable-gross-weight-and-weight-categories/"
                    sx={{ color: '#0202ff' }}
                  >
                    {' '}
                    gross weights
                  </Link>{' '}
                  of 55,000 pounds and above and operated over public roads.
                  While single-vehicle owners enjoy the simplicity of handling a
                  singular piece of data, fleet managers enjoy an added
                  complexity of reporting multiple vehicles with each vehicle
                  having its own Vehicle Identification Number (VIN), weight
                  category, first-used month and it is privately purchased. Form
                  2290 filing accurately keeps all the trucks in the fleet up to
                  date that are legally registered and available to go—avoiding
                  delays, penalties, and expensive downtime.
                </Typography>
                <Box
                  component="div"
                  sx={{ mb: 4, p: 2, borderRadius: 2 }}
                  className="gradient-bg"
                >
                  <Stack
                    direction={{ xs: 'column', md: 'row' }}
                    spacing={2}
                    justifyContent="space-between"
                    alignItems="center"
                  >
                    <Box sx={{ width: '100%' }}>
                      <Typography
                        variant="body1"
                        sx={{ fontWeight: 600, mb: 1 }}
                      >
                        Filing for a Fleet? Don’t Do It Alone.
                      </Typography>
                      <Typography variant="body2">
                        Use TaxZerone to file Form 2290 for multiple vehicles
                        within minutes—simple, fast, and IRS-authorized.
                      </Typography>
                    </Box>
                    <Box
                      sx={{ width: '100%', maxWidth: 150, textAlign: 'center' }}
                    >
                      <Button
                        variant="contained"
                        sx={{
                          textTransform: 'none',
                          boxShadow: 'none',
                          borderRadius: 2,
                          backgroundColor: '#fff',
                          color: '#333',
                          '&:hover, &:focus, &:active': {
                            color: '#fff',
                            background: '#172b6b',
                          },
                        }}
                        size="large"
                        href={`${process.env.NEXT_PUBLIC_APPLICATION_URL}signup/?ref=fleet`}
                      >
                        E-file now
                      </Button>
                    </Box>
                  </Stack>
                </Box>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  ðŸ‘‰ Need assistance with the 2290 form? Our step-by-step{' '}
                  <Link
                    href="/resources/excise-tax/form-2290-instructions/"
                    sx={{ color: '#0202ff' }}
                  >
                    {' '}
                    Form 2290 instructions
                  </Link>{' '}
                  make it easy to file correctly.{' '}
                </Typography>

                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                    scrollMarginTop: 87,
                  }}
                  id="why"
                >
                  Why Fleet Managers Need to File Form 2290?
                </Typography>
                <Box component="ul" sx={{ mb: 3, '& li': { mb: 1.2 } }}>
                  <li>
                    Fleet managers are responsible for ensuring that every heavy
                    vehicle in their fleet complies with IRS regulations by
                    <Link
                      href="/excise-tax/efile-irs-form-2290-heavy-vehicle-use-tax-online/"
                      sx={{ color: '#0202ff' }}
                    >
                      {' '}
                      filing Form 2290
                    </Link>
                    .
                  </li>
                  <li>
                    Since fleets often include many trucks, each with different
                    weights, VINs, and usage details, accurate and timely filing
                    is essential to avoid penalties and registration
                    delays.{' '}
                  </li>
                  <li>
                    Proper filing helps keep the fleet legally compliant,
                    reduces downtime, and ensures smooth business
                    operations.{' '}
                  </li>
                  <li>
                    Managing these filings efficiently protects the company from
                    fines and helps maintain a strong reputation with regulatory
                    authorities.{' '}
                  </li>
                </Box>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                    scrollMarginTop: 87,
                  }}
                  id="manage"
                >
                  Managing Multiple Vehicle Categories
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Not all of the trucks in your fleet are taxed the same. Each
                  one needs to be reported in the correct vehicle category on
                  Form 2290 so you&apos;re only paying what you owe and filing
                  correctly.
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  {' '}
                  Here are the major{' '}
                  <Link
                    href="/resources/excise-tax/form-2290-taxable-suspended-vehicles/"
                    sx={{ color: '#0202ff' }}
                  >
                    vehicle categories
                  </Link>{' '}
                  you may have to manage:{' '}
                </Typography>
                <Box component="ul" sx={{ mb: 2, '& li': { mb: 1.5 } }}>
                  <li>
                    <span style={{ fontWeight: 600 }}> Taxable vehicles:</span>{' '}
                    Over 55,000 pounds and fully taxable.
                  </li>
                  <li>
                    <span style={{ fontWeight: 600 }}>
                      {' '}
                      Suspended vehicles:
                    </span>{' '}
                    Vehicles driven{' '}
                    <span style={{ fontWeight: 600 }}>
                      5,000 miles or less (7,500 miles for agricultural
                      vehicles)
                    </span>{' '}
                    during the tax year may qualify as suspended vehicles.
                  </li>
                </Box>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Make sure you know which category the vehicle belongs to in
                  order to file accordingly.
                </Typography>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                    scrollMarginTop: 87,
                  }}
                  id="howto"
                >
                  How to Handle Taxable Vehicles?
                </Typography>

                <Typography variant="body1" sx={{ mb: 3 }}>
                  Make sure to identify all vehicles that weigh over 55,000
                  pounds and keep their information up to date. File Form 2290
                  on time for each vehicle, correct any mistakes by filing{' '}
                  <Link
                    href="https://form2290.taxzerone.com/form-2290-amendments/"
                    target="_blank"
                    sx={{ color: '#0202ff' }}
                  >
                    {' '}
                    2290 amendments
                  </Link>
                  , and always keep your IRS Schedule 1 for registration
                  purposes.
                </Typography>

                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                    scrollMarginTop: 87,
                  }}
                  id="handle"
                >
                  How to Handle Suspended Vehicles?
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  If certain trucks in your fleet didn’t exceed 5,000 miles on
                  public roads, you can list them as{' '}
                  <Link
                    href="/excise-tax/free-e-file-form-2290-suspended-vehicles/"
                    sx={{ color: '#0202ff' }}
                  >
                    {' '}
                    suspended vehicles
                  </Link>{' '}
                  to lower your tax. TaxZerone makes it easy to mark or identify
                  suspended vehicles (those driven 5,000 miles or less) during
                  the e-filing process.
                </Typography>

                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                    scrollMarginTop: 87,
                  }}
                  id="errors"
                >
                  Correcting Errors: VIN Corrections and Amendments
                </Typography>

                <Box component="ul" sx={{ mb: 3, '& li': { mb: 1.5 } }}>
                  <li>
                    {' '}
                    <span style={{ fontWeight: 600 }}>
                      {' '}
                      VIN Corrections:
                    </span>{' '}
                  </li>
                  <ul>
                    <li>
                      {' '}
                      <span style={{ fontWeight: 600 }}>
                        {' '}
                        Entered the wrong VIN?
                      </span>{' '}
                      You can file{' '}
                      <Link
                        href="/excise-tax/efile-irs-form-2290-vin-correction-online/"
                        sx={{ color: '#0202ff' }}
                      >
                        {' '}
                        FREE VIN corrections
                      </Link>{' '}
                      to update the details on your Form 2290.{' '}
                    </li>
                  </ul>
                  <li>
                    <span style={{ fontWeight: 600 }}>Amendments: </span>
                  </li>
                  <ul>
                    <li>
                      <span style={{ fontWeight: 600 }}>
                        {' '}
                        <Link
                          href="/excise-tax/efile-irs-form-2290-mileage-exceeded-amendment/"
                          sx={{ color: '#0202ff' }}
                        >
                          Mileage Exceeded:
                        </Link>
                      </span>{' '}
                      If your vehicle exceeds the 5,000 miles limit (7,500 for
                      agricultural), file an amendment and pay the HVUT.{' '}
                    </li>
                    <li>
                      {' '}
                      <span style={{ fontWeight: 600 }}>
                        <Link
                          href="/excise-tax/efile-irs-form-2290-weight-increased-amendment/"
                          sx={{ color: '#0202ff' }}
                        >
                          Weight Increased:
                        </Link>
                      </span>{' '}
                      If your truck moves to a higher weight category, file an
                      amendment and pay the difference in tax.{' '}
                    </li>
                  </ul>
                </Box>
                <Box
                  component="div"
                  sx={{
                    mb: 4,
                    p: 2,
                    borderRadius: 2,
                    backgroundColor: '#f9f9f9',
                    backgroundSize: {
                      xs: '20% auto',
                      sm: '20% auto',
                      md: '20% auto',
                    },
                    border: '1px solid #ccc',
                  }}
                >
                  <Box sx={{ width: '100%' }}>
                    <Typography
                      variant="h3"
                      sx={{
                        fontWeight: 600,
                        mb: 1,
                        fontSize: '18px !important',
                      }}
                    >
                      Have doubts about filing, fixing errors, or making
                      amendments?
                    </Typography>
                    <Typography variant="body2">
                      Check out our YouTube channel for step-by-step videos on
                      how to handle common filing issues and submit amendments
                      correctly.
                    </Typography>
                  </Box>
                  <Box sx={{ width: '100%', textAlign: 'left', mt: 2 }}>
                    ðŸ‘‰Watch here:{' '}
                    <Link
                      href="https://form2290.taxzerone.com/form-2290-videos"
                      target="_blank"
                      sx={{ color: '#0202ff' }}
                    >
                      {' '}
                      https://form2290.taxzerone.com/form-2290-videos
                    </Link>
                  </Box>
                </Box>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                    scrollMarginTop: 87,
                  }}
                  id="claim"
                >
                  Claiming Credits and Refunds with Form 8849
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  If your vehicle was sold, stolen, or destroyed you might be
                  entitled to a refund on{' '}
                  <Link
                    href="/excise-tax/efile-irs-form-8849-schedule-6-online/"
                    sx={{ color: '#0202ff' }}
                  >
                    {' '}
                    Form 8849
                  </Link>
                  . Keep these instances in check and file punctually to get
                  your refund.
                </Typography>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                    scrollMarginTop: 87,
                  }}
                  id="helps"
                >
                  How TaxZerone Helps your Fleet Managers?
                </Typography>
                <Typography variant="body1">
                  TaxZerone fleet management tools help make your life simpler
                  by:{' '}
                </Typography>
                <Box component="ul" sx={{ mb: 3, '& li': { mb: 1.2 } }}>
                  <li>
                    Enabling bulk filing for dozens of vehicles at a time.
                  </li>
                  <li>
                    Automatic tracking of your filing deadlines and status.
                  </li>
                  <li>Easy handling of taxable and suspended vehicles. </li>
                  <li>Securing all your tax documents in one place. </li>
                  <li>Offering expert guidance when you need it. </li>
                </Box>
                <Box
                  component="div"
                  sx={{
                    mb: 4,
                    p: 2,
                    borderRadius: 2,
                    backgroundColor: '#f9f9f9',
                    backgroundSize: {
                      xs: '20% auto',
                      sm: '20% auto',
                      md: '20% auto',
                    },
                    border: '1px solid #ccc',
                  }}
                >
                  <Box sx={{ width: '100%' }}>
                    <Typography
                      variant="h3"
                      sx={{
                        fontWeight: 600,
                        mb: 1,
                        fontSize: '18px !important',
                      }}
                    >
                      ðŸ’¡ Need a quick guide?
                    </Typography>
                    <Typography variant="body2">
                      Watch our easy step-by-step video on YouTube to see how
                      fleet managers can simplify Form 2290 filing.
                    </Typography>
                  </Box>
                  <Box sx={{ width: '100%', textAlign: 'left', mt: 2 }}>
                    ðŸ‘‰<span style={{ fontWeight: 600 }}>Watch here:</span>{' '}
                    <Link
                      href="https://www.youtube.com/watch?v=QnUmGYD2yLw"
                      target="_blank"
                      sx={{ color: '#0202ff' }}
                    >
                      {' '}
                      https://www.youtube.com/watch?v=QnUmGYD2yLw
                    </Link>
                  </Box>
                </Box>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                    scrollMarginTop: 87,
                  }}
                  id="best"
                >
                  Best Practices for tax Form 2290 Filing
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  It is advisable to adopt best practices such as keeping your
                  vehicle list updated, sharing filing tasks with your team, and
                  setting reminders before the Form 2290 deadline. Using
                  TaxZerone’s bulk upload and error check features makes filing
                  easier and helps avoid mistakes.
                </Typography>

                <Alert severity="info" icon={false} sx={{ borderRadius: 2 }}>
                  <Accordion
                    defaultExpanded
                    sx={{
                      '&.MuiAccordion-root::before, &.MuiAccordion-root.Mui-expanded::before':
                        {
                          background: 'none !important',
                        },
                      '&.MuiAccordion-root': { background: 'none !important' },
                    }}
                  >
                    <AccordionSummary
                      expandIcon={<ExpandMoreIcon />}
                      aria-controls=""
                      id=""
                      sx={{
                        px: 0,
                        m: 0,
                        justifyContent: 'flex-start',
                        '& .MuiAccordionSummary-content, & .MuiAccordionSummary-content.Mui-expanded':
                          { m: '0 !important', flexGrow: 0 },
                      }}
                    >
                      <Typography
                        variant="body1"
                        sx={{
                          fontWeight: 600,
                          color: '#014361',
                          fontSize: 16,
                        }}
                      >
                        Source & Reference
                      </Typography>
                    </AccordionSummary>
                    <AccordionDetails sx={{ p: 0 }}>
                      <Typography
                        component="div"
                        variant="body2"
                        sx={{ mt: 1 }}
                      >
                        The given above information is based on IRS guidelines.
                        For more details, please refer to the <br />
                        <Link
                          href="https://www.irs.gov/pub/irs-pdf/i2290.pdf"
                          target="_blank"
                          sx={{ color: '#0202ff' }}
                        >
                          {' '}
                          IRS instructions
                        </Link>{' '}
                        of Form 2290.
                      </Typography>
                    </AccordionDetails>
                  </Accordion>
                </Alert>
              </Paper>
            </Grid>
          </Grid>
        </Container>
      </Grid>{' '}
      <Container maxWidth="lg" sx={{ mb: 5, my: { xs: 6, sm: 6, md: 6 } }}>
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          spacing={{ xs: 2, md: 5 }}
        >
          <Box
            sx={{
              width: '100%',
              textAlign: 'center',
            }}
          >
            <Typography
              variant="h2"
              sx={{
                fontSize: { lg: 34, md: 30, sm: 26, xs: 22 },
                fontWeight: 600,
                mb: 2,
                textAlign: 'center',
                lineHeight: 1.5,
              }}
            >
              Ready to File Form 2290 for Your Fleet?
            </Typography>
            <Typography
              sx={{
                fontSize: { lg: 20, md: 18, sm: 16, xs: 16 },
                fontWeight: 500,
                color: '#333333',
                mb: 2,
              }}
            >
              With <span style={{ fontWeight: 600 }}> TaxZerone</span>, you can
              file Form 2290 for multiple vehicles in
              <br />
              minutes—securely, accurately, and with full IRS authorization.
            </Typography>

            <Box sx={{ textAlign: { xs: 'center', md: 'center' } }}>
              <Button
                variant="contained"
                sx={{
                  mt: 1,
                  transition: 'all 0.3s ease 0s',
                  '&:hover, &:focus, &:active': { transform: 'scale(1.1)' },
                }}
                className="btn-primary"
                href={`${process.env.NEXT_PUBLIC_APPLICATION_URL}signup/?ref=fleet`}
              >
                E-file Now
              </Button>
            </Box>
          </Box>
        </Stack>
      </Container>
    </AppLayout>
  );
};

export default FleetManagers;
